import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AddOutlined from '@mui/icons-material/AddOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { Box, ClickAwayListener, Paper, Popper, Typography } from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import { normalizeSx } from '@/shared/lib/normalizeSx';

import {
  disabledMenuItemStyles,
  getDashboardItemStyles,
  MENU_OFFSET,
  menuItemStyles,
  menuItemTextStyles,
  paperBaseSx,
  SELECTOR_BOX_SHADOW
} from './DashboardTargetMenu.styles';
import type { DashboardTargetMenuProps, TabInfo } from './DashboardTargetMenu.types';

export type {
  TabInfo,
  CreateDashboardResult,
  DashboardTargetMenuVariant,
  DashboardTargetMenuProps
} from './DashboardTargetMenu.types';

const NEW_DASHBOARD_KEY = '__new_dashboard__';

// ============================================================================
// Internal hook — hover state for dashboard list + submenu
// ============================================================================

function useDashboardSubmenuHover() {
  const [hoveredDashboard, setHoveredDashboard] = useState<string | null>(null);
  const [hoveredDashboardRef, setHoveredDashboardRef] = useState<HTMLElement | null>(null);
  const dashboardItemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const subMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setDashboardRef = useCallback((key: string, el: HTMLElement | null) => {
    if (el) {
      dashboardItemRefs.current.set(key, el);
    } else {
      dashboardItemRefs.current.delete(key);
    }
  }, []);

  const setHovered = useCallback((key: string | null) => {
    setHoveredDashboard(key);
    setHoveredDashboardRef(key ? (dashboardItemRefs.current.get(key) ?? null) : null);
  }, []);

  const resetHover = useCallback(() => {
    if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
    setHovered(null);
  }, [setHovered]);

  const handleDashboardMouseEnter = useCallback(
    (dashboardName: string) => {
      if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
      setHovered(dashboardName);
    },
    [setHovered]
  );

  const handleDashboardMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      setHovered(null);
    }, 150);
  }, [setHovered]);

  const handleSubMenuMouseEnter = useCallback(() => {
    if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
  }, []);

  const handleSubMenuMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      setHovered(null);
    }, 150);
  }, [setHovered]);

  return {
    hoveredDashboard,
    hoveredDashboardRef,
    setDashboardRef,
    handleDashboardMouseEnter,
    handleDashboardMouseLeave,
    handleSubMenuMouseEnter,
    handleSubMenuMouseLeave,
    resetHover
  };
}

// ============================================================================
// Internal component — right-side tab submenu
// ============================================================================

interface DashboardTabSubmenuProps {
  anchorEl: HTMLElement;
  dashboardName: string;
  tabs: TabInfo[];
  showNewPageOption: boolean;
  disabled: boolean;
  zIndex: number;
  backgroundColor: unknown;
  onTabClick: (dashboardName: string, tabId: string, tabName: string) => void;
  onNewTabClick: (dashboardName: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const DashboardTabSubmenu = ({
  anchorEl,
  dashboardName,
  tabs,
  showNewPageOption,
  disabled,
  zIndex,
  backgroundColor,
  onTabClick,
  onNewTabClick,
  onMouseEnter,
  onMouseLeave
}: DashboardTabSubmenuProps) => (
  <Popper
    key={dashboardName}
    open
    anchorEl={anchorEl}
    modifiers={[
      { name: 'offset', options: { offset: MENU_OFFSET } },
      {
        name: 'revealAfterPositioned',
        enabled: true,
        phase: 'afterWrite',
        fn({ state }: { state: { elements: { popper: HTMLElement } } }) {
          state.elements.popper.style.visibility = 'visible';
        }
      }
    ]}
    placement='right-start'
    sx={{ visibility: 'hidden', zIndex }}
  >
    <Paper
      sx={{
        backgroundColor: backgroundColor ?? 'gx.tileLight',
        minWidth: 180,
        boxShadow: SELECTOR_BOX_SHADOW
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {tabs.map(({ tabName, tabId }) => (
        <Box
          key={tabId}
          sx={disabled ? disabledMenuItemStyles : menuItemStyles}
          onClick={() => {
            onTabClick(dashboardName, tabId, tabName);
          }}
        >
          <Typography sx={menuItemTextStyles}>{tabName}</Typography>
        </Box>
      ))}
      {showNewPageOption && (
        <Box
          sx={disabled ? disabledMenuItemStyles : menuItemStyles}
          onClick={() => {
            onNewTabClick(dashboardName);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddOutlined sx={{ fontSize: 18 }} />
            <Typography sx={menuItemTextStyles}>New page</Typography>
          </Box>
        </Box>
      )}
    </Paper>
  </Popper>
);

// ============================================================================
// Component
// ============================================================================

export const DashboardTargetMenu = ({
  anchorEl,
  open,
  onClose,
  variant = 'with-tabs',
  excludeCurrentTabId: _excludeCurrentTabId,
  showNewPageOption = true,
  showChevron = true,
  onSelectTab,
  onCreateNewTab,
  onSelectDashboard,
  filterTab: _filterTab,
  emptyMessage = 'No other dashboards',
  disabled = false,
  onCreateDashboard,
  dashboards,
  getTabs,
  currentDashboardName: customCurrentDashboardName,
  onError,
  headerText,
  paperSx,
  popperSx,
  placement = 'bottom-start',
  offset: offsetProp = MENU_OFFSET,
  zIndexWhenBelow = Z_INDEX.panel,
  zIndexWhenAbove = Z_INDEX.panel
}: DashboardTargetMenuProps) => {
  const subMenuBg =
    paperSx && !Array.isArray(paperSx) && typeof paperSx === 'object'
      ? (paperSx as { backgroundColor?: unknown }).backgroundColor
      : undefined;

  const [isFlippedToTop, setIsFlippedToTop] = useState(false);
  const effectiveZIndex = isFlippedToTop ? zIndexWhenAbove : zIndexWhenBelow;

  const {
    hoveredDashboard,
    hoveredDashboardRef,
    setDashboardRef,
    handleDashboardMouseEnter,
    handleDashboardMouseLeave,
    handleSubMenuMouseEnter,
    handleSubMenuMouseLeave,
    resetHover
  } = useDashboardSubmenuHover();

  useEffect(() => {
    if (!open) resetHover();
  }, [open, resetHover]);

  const dashboardNamesList = useMemo(
    () =>
      [...dashboards].sort((a, b) => {
        if (variant === 'with-tabs') {
          if (a === customCurrentDashboardName) return -1;
          if (b === customCurrentDashboardName) return 1;
        }
        return a.localeCompare(b);
      }),
    [dashboards, customCurrentDashboardName, variant]
  );

  const hoveredDashboardTabs = useMemo(() => {
    if (!hoveredDashboard || variant === 'dashboard-only') return null;
    const tabs = getTabs(hoveredDashboard);
    if (!tabs && showNewPageOption) return [];
    return tabs;
  }, [hoveredDashboard, variant, getTabs, showNewPageOption]);

  const handleTabClick = useCallback(
    (dashboardName: string, tabId: string, tabName: string) => {
      if (disabled) return;
      onSelectTab?.(dashboardName, { tabId, tabName });
      onClose();
    },
    [disabled, onSelectTab, onClose]
  );

  const handleNewTabClick = useCallback(
    (dashboardName: string) => {
      if (disabled) return;
      onCreateNewTab?.(dashboardName);
      onClose();
    },
    [disabled, onCreateNewTab, onClose]
  );

  const handleDashboardClick = useCallback(
    (dashboardName: string) => {
      if (disabled || variant !== 'dashboard-only') return;
      onSelectDashboard?.(dashboardName);
      onClose();
    },
    [disabled, variant, onSelectDashboard, onClose]
  );

  const handleCreateNewDashboard = useCallback(async () => {
    if (disabled || !onCreateDashboard) return;

    const result = await onCreateDashboard();
    if (!result) return;

    if (!result.ok) {
      onError?.(
        result.message ||
          (result.reason
            ? `Failed to create dashboard: ${result.reason}`
            : 'Failed to create dashboard')
      );
      return;
    }

    if (!result.dashboardName) {
      onError?.('Failed to create dashboard: missing name.');
      return;
    }

    if (variant === 'with-tabs') {
      if (!onCreateNewTab) {
        onError?.('Failed to create dashboard: missing page handler.');
        return;
      }
      onCreateNewTab(result.dashboardName);
    } else {
      onSelectDashboard?.(result.dashboardName);
    }
    onClose();
  }, [disabled, onCreateDashboard, variant, onCreateNewTab, onSelectDashboard, onClose, onError]);

  return (
    <Popper
      anchorEl={anchorEl}
      modifiers={[
        { name: 'offset', options: { offset: offsetProp } },
        {
          name: 'trackPlacement',
          enabled: true,
          phase: 'afterWrite',
          fn({ state }: { state: { placement: string } }) {
            setIsFlippedToTop(state.placement.startsWith('top'));
          }
        }
      ]}
      open={open}
      placement={placement}
      sx={[{ zIndex: effectiveZIndex }, ...normalizeSx(popperSx)]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper sx={[paperBaseSx, ...normalizeSx(paperSx)]}>
          {headerText && (
            <Box
              sx={{
                px: 1,
                py: 0.75,
                borderTop: '1px solid rgba(255,255,255,0.1)',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>{headerText}</Typography>
            </Box>
          )}

          {dashboardNamesList.length === 0 && !onCreateDashboard ? (
            <Box sx={disabledMenuItemStyles}>{emptyMessage}</Box>
          ) : (
            <>
              {dashboardNamesList.map(dashboardName => (
                <Box
                  key={dashboardName}
                  ref={(el: HTMLDivElement | null) => {
                    setDashboardRef(dashboardName, el);
                  }}
                  sx={getDashboardItemStyles(disabled, hoveredDashboard === dashboardName)}
                  onClick={() => {
                    handleDashboardClick(dashboardName);
                  }}
                  onMouseEnter={
                    variant === 'with-tabs'
                      ? () => {
                          handleDashboardMouseEnter(dashboardName);
                        }
                      : undefined
                  }
                  onMouseLeave={variant === 'with-tabs' ? handleDashboardMouseLeave : undefined}
                >
                  <Typography sx={menuItemTextStyles}>{dashboardName}</Typography>
                  {showChevron && variant === 'with-tabs' && (
                    <ChevronRight sx={{ fontSize: 20, opacity: 0.7 }} />
                  )}
                </Box>
              ))}

              {onCreateDashboard && (
                <Box
                  ref={(el: HTMLDivElement | null) => {
                    setDashboardRef(NEW_DASHBOARD_KEY, el);
                  }}
                  sx={disabled ? disabledMenuItemStyles : menuItemStyles}
                  onClick={handleCreateNewDashboard}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddOutlined sx={{ fontSize: 18 }} />
                    <Typography sx={menuItemTextStyles}>New Dashboard</Typography>
                  </Box>
                </Box>
              )}
            </>
          )}

          {variant === 'with-tabs' &&
            hoveredDashboard &&
            hoveredDashboardTabs !== null &&
            hoveredDashboardRef && (
              <DashboardTabSubmenu
                anchorEl={hoveredDashboardRef}
                backgroundColor={subMenuBg}
                dashboardName={hoveredDashboard}
                disabled={disabled}
                showNewPageOption={showNewPageOption}
                tabs={hoveredDashboardTabs}
                zIndex={effectiveZIndex + 1}
                onMouseEnter={handleSubMenuMouseEnter}
                onMouseLeave={handleSubMenuMouseLeave}
                onNewTabClick={handleNewTabClick}
                onTabClick={handleTabClick}
              />
            )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default DashboardTargetMenu;
